import { useSyncExternalStore } from 'react';

/**
 * Translations. English is the default; German and Russian are switchable in
 * Settings.
 *
 * Deliberately no i18n library: three languages and a few dozen strings do not
 * justify a dependency that ships a parser, plural rules and a loader. Every
 * dependency in this client is code delivered to users and has to be trusted —
 * the bar is higher here than in an ordinary web app.
 *
 * `en` is the source of truth. Its keys define the shape and the other
 * languages are typed against it, so a forgotten string is a compile error
 * rather than a gap someone notices in production.
 */
const en = {
  // ── Sign-in ────────────────────────────────────────────────────────────
  appName: 'Webmessenger',
  username: 'Username',
  password: 'Password',
  signIn: 'Sign in',
  signingIn: 'Signing in…',
  generatingKeys: 'Generating keys…',
  registeringDevice: 'Registering device…',
  createAccount: 'Create account',
  creatingAccount: 'Creating account…',
  backToSignIn: 'Back to sign in',
  email: 'Email',
  displayName: 'Display name',
  displayNameHint: 'This is how your contacts see you. Nobody sees your email.',
  changePassword: 'Change password',
  changePasswordLead:
    'You signed in with a password set by an administrator. They know it, so it has to be replaced before this device is registered.',
  currentPassword: 'Current password',
  newPassword: 'New password',
  repeatPassword: 'Repeat password',
  saveAndContinue: 'Save and continue',
  changingPassword: 'Changing password…',

  // ── Errors ─────────────────────────────────────────────────────────────
  errInvalidCredentials: 'Wrong username or password.',
  deviceRemoved:
    'This device was removed by an administrator. Sign in again to set it up anew.',
  errIdentifierTaken: 'An account with this email already exists.',
  errRegistrationClosed: 'New accounts are currently not enabled.',
  errPasswordUnchanged: 'The new password must differ from the old one.',
  errAccountSuspended: 'This account is suspended.',
  errPasswordTooShort: 'The password must be at least 8 characters.',
  errPasswordsDiffer: 'The two passwords do not match.',
  errContactGone: 'Contact is no longer in the directory.',
  errNoDeviceReachable: 'No device of the other side is reachable.',
  errSendFailed: 'Sending failed',
  errFetchFailed: 'Fetching failed',
  errCallFailed: 'Call failed',
  errContactsFailed: 'Loading contacts failed',
  errSyncPartial: 'Message delivered, but not to your other devices.',

  // ── Chat ───────────────────────────────────────────────────────────────
  chats: 'Chats',
  search: 'Search',
  thisDevice: 'This device',
  settings: 'Settings',
  noContacts:
    'Nobody else is set up yet. Create a second user in the admin console and set them up in another browser window.',
  noContactsFound: 'No contact found.',
  noMessagesYet: 'Encrypted — no messages yet',
  youPrefix: 'You: ',
  pickContact:
    'Pick a contact on the left. Every message is encrypted on this device — the server only sees an unreadable block.',
  writeMessage: 'Write a message…',
  send: 'Send',
  back: 'Back to list',
  endToEndEncrypted: 'End-to-end encrypted',
  today: 'Today',
  yesterday: 'Yesterday',
  delivered: 'Delivered',
  read: 'Read',

  // ── Safety number ──────────────────────────────────────────────────────
  copy: 'Copy',
  cancelSelection: 'Cancel selection',
  copied: 'Copied',
  compareSafetyNumber: 'Compare safety number',
  safetyNumberHint:
    'Read this number to each other over a different channel. If it matches, nobody is listening in. If it does not, the connection is not trustworthy — stop writing.',
  safetyNumberChanged: 'Safety number changed',
  safetyNumberChangedBody:
    'The safety number of this contact has changed. That happens after a reinstall — or when somebody has taken over the connection. Compare the number over a different channel before you continue.',
  keyAlertLead:
    'Nothing is sent until you confirm. Compare the number over a different channel — by phone, in person. If it does not match, somebody is listening.',
  acceptNewKey: 'I checked — accept the new key',

  // ── Calls ──────────────────────────────────────────────────────────────
  calling: 'Calling…',
  incomingCall: 'Incoming call',
  connecting: 'Connecting…',
  connected: 'Connected',
  callEnded: 'Ended',
  accept: 'Accept',
  reject: 'Reject',
  hangUp: 'Hang up',
  muteOn: 'Turn microphone on',
  muteOff: 'Turn microphone off',
  switchCamera: 'Switch camera',
  fullscreen: 'Fullscreen',
  exitFullscreen: 'Exit fullscreen',
  voiceCall: 'Voice call',
  videoCall: 'Video call',
  callNote:
    'Voice and video are encrypted directly between you. The server cannot even tell that a call is happening.',
  callBusy: 'The other side is already on a call.',
  callRejected: 'Call rejected.',
  callConnectionFailed: 'Connection failed.',
  errNoCamera: 'No camera or microphone found.',
  errPermissionDenied: 'Access to camera or microphone was denied.',

  // ── Settings ───────────────────────────────────────────────────────────
  cameraOn: 'Turn camera on',
  cameraOff: 'Turn camera off',
  minimise: 'Back to chat',
  returnToCall: 'Return to call',
  ongoingCall: 'Call in progress',
  language: 'Language',
  languageHint: 'Applies immediately and is remembered on this device.',
  account: 'Account',
  signOut: 'Sign out',
  signOutNote: 'Ends the session. Keys and history stay in this browser.',
  close: 'Close',
  deviceNote: 'This device was set up in this browser.',
  repeat: 'Repeat',
  changing: 'Changing…',
  passwordChanged: 'Password changed.',
  errCurrentPassword: 'The current password is wrong.',
  passwordNote:
    'The password protects sign-in, not your messages. Those sit encrypted in this browser and do not depend on it — changing the password makes them neither readable nor unreadable.',
  resetDevice: 'Reset device',
  resetDeviceNote:
    'Deletes the keys and the entire history in this browser. This cannot be undone — there is no backup. Your contacts will see a changed safety number afterwards.',
  confirmWipe: 'Yes, delete everything',
  cancel: 'Cancel',
  reset: 'Reset',
} as const;

/**
 * Every language provides exactly the keys of `en`, as plain strings.
 *
 * Not `typeof en`: `as const` turns each English value into a literal type, so
 * a translation would have to repeat the English word to satisfy it. What
 * matters is the SET of keys — a missing one must fail the build, a different
 * wording must not.
 */
type Dictionary = Record<keyof typeof en, string>;

const de: Dictionary = {
  appName: 'Webmessenger',
  username: 'Benutzername',
  password: 'Passwort',
  signIn: 'Anmelden',
  signingIn: 'Melde an…',
  generatingKeys: 'Erzeuge Schlüssel…',
  registeringDevice: 'Registriere Gerät…',
  createAccount: 'Konto anlegen',
  creatingAccount: 'Lege Konto an…',
  backToSignIn: 'Zurück zur Anmeldung',
  email: 'E-Mail',
  displayName: 'Anzeigename',
  displayNameHint: 'So sehen dich deine Kontakte. Deine E-Mail sieht niemand.',
  changePassword: 'Passwort ändern',
  changePasswordLead:
    'Du hast dich mit einem Passwort angemeldet, das ein Administrator vergeben hat. Er kennt es — deshalb muss es ersetzt werden, bevor dieses Gerät eingerichtet wird.',
  currentPassword: 'Aktuelles Passwort',
  newPassword: 'Neues Passwort',
  repeatPassword: 'Passwort wiederholen',
  saveAndContinue: 'Speichern und fortfahren',
  changingPassword: 'Ändere Passwort…',

  errInvalidCredentials: 'Name oder Passwort stimmt nicht.',
  deviceRemoved:
    'Dieses Gerät wurde von einem Administrator entfernt. Melde dich erneut an, um es neu einzurichten.',
  errIdentifierTaken: 'Für diese E-Mail-Adresse gibt es bereits ein Konto.',
  errRegistrationClosed: 'Neue Konten sind derzeit nicht freigeschaltet.',
  errPasswordUnchanged: 'Das neue Passwort muss sich vom alten unterscheiden.',
  errAccountSuspended: 'Dieses Konto ist gesperrt.',
  errPasswordTooShort: 'Das Passwort muss mindestens 8 Zeichen haben.',
  errPasswordsDiffer: 'Die beiden Passwörter stimmen nicht überein.',
  errContactGone: 'Kontakt nicht mehr im Verzeichnis.',
  errNoDeviceReachable: 'Kein Gerät der Gegenstelle erreichbar.',
  errSendFailed: 'Senden fehlgeschlagen',
  errFetchFailed: 'Abholen fehlgeschlagen',
  errCallFailed: 'Anruf fehlgeschlagen',
  errContactsFailed: 'Kontakte laden fehlgeschlagen',
  errSyncPartial: 'Die Nachricht ist zugestellt, aber nicht auf deinen anderen Geräten.',

  chats: 'Chats',
  search: 'Suchen',
  thisDevice: 'Dieses Gerät',
  settings: 'Einstellungen',
  noContacts:
    'Noch niemand sonst eingerichtet. Lege in der Admin-Konsole einen zweiten Benutzer an und richte ihn in einem anderen Browserfenster ein.',
  noContactsFound: 'Kein Kontakt gefunden.',
  noMessagesYet: 'Verschlüsselt — noch keine Nachrichten',
  youPrefix: 'Du: ',
  pickContact:
    'Wähle links einen Kontakt. Jede Nachricht wird auf diesem Gerät verschlüsselt — der Server sieht nur einen unlesbaren Block.',
  writeMessage: 'Nachricht schreiben…',
  send: 'Senden',
  back: 'Zurück zur Liste',
  endToEndEncrypted: 'Ende-zu-Ende verschlüsselt',
  today: 'Heute',
  yesterday: 'Gestern',
  delivered: 'Zugestellt',
  read: 'Gelesen',

  copy: 'Kopieren',
  cancelSelection: 'Auswahl aufheben',
  copied: 'Kopiert',
  compareSafetyNumber: 'Sicherheitsnummer vergleichen',
  safetyNumberHint:
    'Lest diese Zahl über einen anderen Kanal gegenseitig vor. Stimmt sie überein, hört niemand mit. Stimmt sie nicht überein, ist die Verbindung nicht vertrauenswürdig — dann nicht weiterschreiben.',
  safetyNumberChanged: 'Sicherheitsnummer geändert',
  safetyNumberChangedBody:
    'Die Sicherheitsnummer dieses Kontakts hat sich geändert. Das passiert bei einer Neuinstallation — oder wenn jemand die Verbindung übernommen hat. Vergleicht die Nummer über einen anderen Kanal, bevor ihr weiterschreibt.',
  keyAlertLead:
    'Bis zur Bestätigung wird nichts gesendet. Vergleicht die Nummer über einen anderen Kanal — Telefon, persönlich. Stimmt sie nicht, hört jemand mit.',
  acceptNewKey: 'Ich habe geprüft — neuen Schlüssel annehmen',

  calling: 'Ruft an…',
  incomingCall: 'Eingehender Anruf',
  connecting: 'Verbinde…',
  connected: 'Verbunden',
  callEnded: 'Beendet',
  accept: 'Annehmen',
  reject: 'Ablehnen',
  hangUp: 'Auflegen',
  muteOn: 'Mikrofon einschalten',
  muteOff: 'Mikrofon ausschalten',
  switchCamera: 'Kamera wechseln',
  fullscreen: 'Vollbild',
  exitFullscreen: 'Vollbild beenden',
  voiceCall: 'Sprachanruf',
  videoCall: 'Videoanruf',
  callNote:
    'Sprache und Bild sind direkt zwischen euch verschlüsselt. Der Server sieht nicht einmal, dass ein Anruf läuft.',
  callBusy: 'Die Gegenseite telefoniert gerade.',
  callRejected: 'Anruf abgelehnt.',
  callConnectionFailed: 'Verbindung fehlgeschlagen.',
  errNoCamera: 'Keine Kamera oder kein Mikrofon gefunden.',
  errPermissionDenied: 'Zugriff auf Kamera oder Mikrofon wurde verweigert.',

  cameraOn: 'Kamera einschalten',
  cameraOff: 'Kamera ausschalten',
  minimise: 'Zurück zum Chat',
  returnToCall: 'Zurück zum Anruf',
  ongoingCall: 'Gespräch läuft',
  language: 'Sprache',
  languageHint: 'Gilt sofort und wird auf diesem Gerät gemerkt.',
  account: 'Konto',
  signOut: 'Abmelden',
  signOutNote: 'Beendet die Sitzung. Schlüssel und Verlauf bleiben in diesem Browser erhalten.',
  close: 'Schließen',
  deviceNote: 'Dieses Gerät wurde in diesem Browser eingerichtet.',
  repeat: 'Wiederholen',
  changing: 'Ändere…',
  passwordChanged: 'Passwort geändert.',
  errCurrentPassword: 'Das aktuelle Passwort stimmt nicht.',
  passwordNote:
    'Das Passwort schützt die Anmeldung, nicht deine Nachrichten. Die liegen verschlüsselt in diesem Browser und hängen nicht daran — ein Passwortwechsel macht sie weder lesbar noch unlesbar.',
  resetDevice: 'Gerät zurücksetzen',
  resetDeviceNote:
    'Löscht Schlüssel und gesamten Verlauf in diesem Browser. Das lässt sich nicht rückgängig machen — es gibt kein Backup. Deine Kontakte sehen danach eine geänderte Sicherheitsnummer.',
  confirmWipe: 'Ja, alles löschen',
  cancel: 'Abbrechen',
  reset: 'Zurücksetzen',
};

const ru: Dictionary = {
  appName: 'Webmessenger',
  username: 'Имя пользователя',
  password: 'Пароль',
  signIn: 'Войти',
  signingIn: 'Вход…',
  generatingKeys: 'Создание ключей…',
  registeringDevice: 'Регистрация устройства…',
  createAccount: 'Создать аккаунт',
  creatingAccount: 'Создание аккаунта…',
  backToSignIn: 'Назад ко входу',
  email: 'Электронная почта',
  displayName: 'Отображаемое имя',
  displayNameHint: 'Так вас видят контакты. Вашу почту не видит никто.',
  changePassword: 'Сменить пароль',
  changePasswordLead:
    'Вы вошли с паролем, который задал администратор. Он его знает, поэтому пароль нужно заменить до регистрации устройства.',
  currentPassword: 'Текущий пароль',
  newPassword: 'Новый пароль',
  repeatPassword: 'Повторите пароль',
  saveAndContinue: 'Сохранить и продолжить',
  changingPassword: 'Смена пароля…',

  errInvalidCredentials: 'Неверное имя пользователя или пароль.',
  deviceRemoved:
    'Это устройство удалил администратор. Войдите снова, чтобы настроить его заново.',
  errIdentifierTaken: 'Аккаунт с этой почтой уже существует.',
  errRegistrationClosed: 'Создание новых аккаунтов сейчас отключено.',
  errPasswordUnchanged: 'Новый пароль должен отличаться от старого.',
  errAccountSuspended: 'Этот аккаунт заблокирован.',
  errPasswordTooShort: 'Пароль должен содержать не менее 8 символов.',
  errPasswordsDiffer: 'Пароли не совпадают.',
  errContactGone: 'Контакта больше нет в каталоге.',
  errNoDeviceReachable: 'Ни одно устройство собеседника недоступно.',
  errSendFailed: 'Не удалось отправить',
  errFetchFailed: 'Не удалось получить',
  errCallFailed: 'Не удалось позвонить',
  errContactsFailed: 'Не удалось загрузить контакты',
  errSyncPartial: 'Сообщение доставлено, но не на ваши другие устройства.',

  chats: 'Чаты',
  search: 'Поиск',
  thisDevice: 'Это устройство',
  settings: 'Настройки',
  noContacts:
    'Больше никто не настроен. Создайте второго пользователя в консоли администратора и настройте его в другом окне браузера.',
  noContactsFound: 'Контакт не найден.',
  noMessagesYet: 'Зашифровано — сообщений пока нет',
  youPrefix: 'Вы: ',
  pickContact:
    'Выберите контакт слева. Каждое сообщение шифруется на этом устройстве — сервер видит только нечитаемый блок.',
  writeMessage: 'Написать сообщение…',
  send: 'Отправить',
  back: 'Назад к списку',
  endToEndEncrypted: 'Сквозное шифрование',
  today: 'Сегодня',
  yesterday: 'Вчера',
  delivered: 'Доставлено',
  read: 'Прочитано',

  copy: 'Копировать',
  cancelSelection: 'Снять выделение',
  copied: 'Скопировано',
  compareSafetyNumber: 'Сверить код безопасности',
  safetyNumberHint:
    'Продиктуйте это число друг другу по другому каналу связи. Если оно совпадает, вас никто не слушает. Если нет — соединению нельзя доверять, прекратите переписку.',
  safetyNumberChanged: 'Код безопасности изменился',
  safetyNumberChangedBody:
    'Код безопасности этого контакта изменился. Так бывает после переустановки — или когда кто-то перехватил соединение. Сверьте код по другому каналу, прежде чем продолжать.',
  keyAlertLead:
    'До подтверждения ничего не отправляется. Сверьте код по другому каналу — по телефону, лично. Если он не совпадает, вас кто-то слушает.',
  acceptNewKey: 'Я проверил — принять новый ключ',

  calling: 'Вызов…',
  incomingCall: 'Входящий вызов',
  connecting: 'Соединение…',
  connected: 'Соединено',
  callEnded: 'Завершено',
  accept: 'Ответить',
  reject: 'Отклонить',
  hangUp: 'Положить трубку',
  muteOn: 'Включить микрофон',
  muteOff: 'Выключить микрофон',
  switchCamera: 'Сменить камеру',
  fullscreen: 'Полный экран',
  exitFullscreen: 'Выйти из полного экрана',
  voiceCall: 'Голосовой вызов',
  videoCall: 'Видеовызов',
  callNote:
    'Звук и видео зашифрованы напрямую между вами. Сервер не видит даже того, что идёт звонок.',
  callBusy: 'Собеседник сейчас разговаривает.',
  callRejected: 'Вызов отклонён.',
  callConnectionFailed: 'Не удалось установить соединение.',
  errNoCamera: 'Камера или микрофон не найдены.',
  errPermissionDenied: 'В доступе к камере или микрофону отказано.',

  cameraOn: 'Включить камеру',
  cameraOff: 'Выключить камеру',
  minimise: 'Назад к чату',
  returnToCall: 'Вернуться к звонку',
  ongoingCall: 'Идёт разговор',
  language: 'Язык',
  languageHint: 'Применяется сразу и запоминается на этом устройстве.',
  account: 'Аккаунт',
  signOut: 'Выйти',
  signOutNote: 'Завершает сеанс. Ключи и переписка остаются в этом браузере.',
  close: 'Закрыть',
  deviceNote: 'Это устройство было настроено в этом браузере.',
  repeat: 'Повторите',
  changing: 'Изменение…',
  passwordChanged: 'Пароль изменён.',
  errCurrentPassword: 'Текущий пароль неверен.',
  passwordNote:
    'Пароль защищает вход, а не ваши сообщения. Они лежат зашифрованными в этом браузере и не зависят от него — смена пароля не делает их ни читаемыми, ни нечитаемыми.',
  resetDevice: 'Сбросить устройство',
  resetDeviceNote:
    'Удаляет ключи и всю переписку в этом браузере. Это необратимо — резервной копии нет. Ваши контакты увидят изменившийся код безопасности.',
  confirmWipe: 'Да, удалить всё',
  cancel: 'Отмена',
  reset: 'Сбросить',
};

export type Lang = 'en' | 'de' | 'ru';
export type Key = keyof typeof en;

const dictionaries = { en, de, ru };

/** Shown in the language picker — each in its own language, never translated. */
export const languageNames: Record<Lang, string> = {
  en: 'English',
  de: 'Deutsch',
  ru: 'Русский',
};

const STORAGE_KEY = 'wm.lang';

function read(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'de' || stored === 'ru') return stored;
  } catch {
    // Private windows and blocked site data throw here. English then.
  }
  return 'en';
}

let current: Lang = read();
const listeners = new Set<() => void>();

export function setLanguage(lang: Lang): void {
  current = lang;
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Not persisting is acceptable; the change still applies to this session.
  }
  document.documentElement.lang = lang;
  for (const listener of listeners) listener();
}

export function getLanguage(): Lang {
  return current;
}

/**
 * Subscribe to language changes.
 *
 * `useSyncExternalStore` rather than a React context: a context would have to
 * be threaded through every component, and the admin console renders outside
 * the app tree entirely. This keeps the change to one line per component.
 */
export function useT(): (key: Key) => string {
  const lang = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => 'en' as Lang,
  );

  return (key: Key) => dictionaries[lang][key];
}

/** Locale for dates and times, derived from the chosen language. */
export function locale(): string {
  return { en: 'en-GB', de: 'de-DE', ru: 'ru-RU' }[current];
}

document.documentElement.lang = current;
